/* Copyright (c) 2022 Xperi. All Rights Reserved. */

import { PreviewType, Provider, ScreenType } from 'client-code/previews';

import { DimensionScaler } from '../device/DimensionScaler';
import { PreviewsServiceRepositoryImpl } from '../impls/PreviewsServiceRepositoryImpl';
import { PreviewsServiceRepositoryImplV2 } from '../impls/PreviewsServiceRepositoryImplV2';
import { IPreviewsService, IPreviewsServiceRepository } from '../interfaces';
import { IRecordingPreviewParams } from '../interfaces/IPreviewService';
import {
    PROVIDER_IMAGE_HEIGHT,
    PROVIDER_IMAGE_NAME,
    PROVIDER_IMAGE_WIDTH,
    RECORDING_PREVIEW_HEIGHT,
    RECORDING_PREVIEW_MOVIE_WIDTH,
    RECORDING_PREVIEW_WIDTH,
} from '../strings';
import {
    TContentPreview,
    TOfferPreview,
    TServicesParams,
    TUnifiedCollectionPreview,
    TUnifiedCollectionPreviewParams,
} from '../types';
import { ImageUrlGenerator } from '../utils/ImageUrlGenerator';

export class PreviewsService implements IPreviewsService {
    private mImageHostBaseUrl: string;

    private oPreviewsServiceRepository: IPreviewsServiceRepository;

    private oPreviewsServiceRepositoryV2: IPreviewsServiceRepository;

    public constructor(params: TServicesParams) {
        this.mImageHostBaseUrl = params.imageHostBaseUrl || '';
        this.oPreviewsServiceRepository = new PreviewsServiceRepositoryImpl(params);
        this.oPreviewsServiceRepositoryV2 = new PreviewsServiceRepositoryImplV2(params);
    }

    private createProviderImageUrls(providersList?: Provider[]): string[] {
        let providersImageUrls: string[] = [];
        if (providersList) {
            providersImageUrls = providersList.map((provider) => {
                return `${this.mImageHostBaseUrl}/images/${provider.providerId}/${PROVIDER_IMAGE_NAME}${DimensionScaler.getInstance().getScaledWidth(
                    PROVIDER_IMAGE_WIDTH,
                )}x${DimensionScaler.getInstance().getScaledHeight(PROVIDER_IMAGE_HEIGHT)}`;
            });
        }
        return providersImageUrls;
    }

    private updateOfferPreviewWithProviderImageUrls(offerPreview: TOfferPreview): TOfferPreview {
        const updatedOfferPreview: TOfferPreview = { ...offerPreview };
        const providersImageUrls = this.createProviderImageUrls(
            offerPreview.offerPreview?.contentPreview?.providersList,
        );
        if (updatedOfferPreview.offerPreview?.contentPreview) {
            updatedOfferPreview.offerPreview.contentPreview.providersImageUrls = providersImageUrls;
        }

        return updatedOfferPreview;
    }

    private updateOfferPreviewWithEpisodeImageUrl(offerPreview: TOfferPreview): TOfferPreview {
        const updatedOfferPreview: TOfferPreview = { ...offerPreview };

        if (updatedOfferPreview.offerPreview.contentPreview) {
            const imageGenerator = new ImageUrlGenerator();

            updatedOfferPreview.offerPreview.contentPreview.episodePreviewImageUrl =
                imageGenerator.buildImageUrl(
                    this.mImageHostBaseUrl,
                    updatedOfferPreview.offerPreview.contentPreview.contentId ||
                        (updatedOfferPreview.offerPreview.contentPreview.collectionId as string),
                    imageGenerator.getImageType(
                        updatedOfferPreview.offerPreview.contentPreview.previewType,
                    ),
                    updatedOfferPreview.offerPreview.contentPreview.previewType ===
                        PreviewType.MoviePreview
                        ? RECORDING_PREVIEW_MOVIE_WIDTH
                        : RECORDING_PREVIEW_WIDTH,
                    RECORDING_PREVIEW_HEIGHT,
                );
        }

        return updatedOfferPreview;
    }

    /**
     * @inheritdoc
     */
    public async fetchOfferPreview(
        offerId: string,
        screenType: ScreenType,
        networkCallId?: number,
    ): Promise<TOfferPreview> {
        const offerPreviewResponse: TOfferPreview =
            await this.oPreviewsServiceRepository.fetchOfferPreview(
                offerId,
                screenType,
                networkCallId,
            );

        return this.updateOfferPreviewWithProviderImageUrls(offerPreviewResponse);
    }

    /**
     * @inheritdoc
     */
    public async fetchOfferPreviewV2(
        offerId: string,
        screenType: ScreenType,
        networkCallId?: number,
    ): Promise<TOfferPreview> {
        const offerPreviewResponse: TOfferPreview =
            await this.oPreviewsServiceRepositoryV2.fetchOfferPreview(
                offerId,
                screenType,
                networkCallId,
            );

        return this.updateOfferPreviewWithProviderImageUrls(offerPreviewResponse);
    }

    /**
     * @inheritdoc
     */
    public async fetchRecordingPreviewV2(
        recordingPreviewParams: IRecordingPreviewParams,
    ): Promise<TOfferPreview> {
        const recordingPreviewResponse: TOfferPreview =
            await this.oPreviewsServiceRepositoryV2.fetchRecordingPreview(recordingPreviewParams);

        const updatedOfferWithProviderImages: TOfferPreview =
            this.updateOfferPreviewWithProviderImageUrls(recordingPreviewResponse);
        return this.updateOfferPreviewWithEpisodeImageUrl(updatedOfferWithProviderImages);
    }

    /**
     * @inheritdoc
     */
    public async fetchVodOfferPreview(
        vodOfferId: string,
        screenType: ScreenType,
        networkCallId?: number,
    ): Promise<TOfferPreview> {
        return this.oPreviewsServiceRepository.fetchVodOfferPreview(
            vodOfferId,
            screenType,
            networkCallId,
        );
    }

    /**
     * @inheritdoc
     */
    public async getContentPreviewV2ForContentId(contentId: string): Promise<TContentPreview> {
        const contentPreviewResponse: TContentPreview =
            await this.oPreviewsServiceRepositoryV2.fetchContentPreview(contentId);
        contentPreviewResponse.providersImageUrls = this.createProviderImageUrls(
            contentPreviewResponse?.providersList,
        );
        return contentPreviewResponse;
    }

    /**
     * @inheritdoc
     */
    public async getPreviewForUCId(
        unifiedCollectionPreviewParams: TUnifiedCollectionPreviewParams,
    ): Promise<TUnifiedCollectionPreview> {
        const unifiedCollectionPreviewResponse: TUnifiedCollectionPreview =
            await this.oPreviewsServiceRepository.fetchUnifiedCollectionPreview(
                unifiedCollectionPreviewParams,
            );
        unifiedCollectionPreviewResponse.providersImageUrls = this.createProviderImageUrls(
            unifiedCollectionPreviewResponse?.providersList,
        );
        return unifiedCollectionPreviewResponse;
    }
}
